import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Campaign, getCampaigns } from "@/api/campaign";
import { PageContainer } from "@/components/PageContainer";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { format } from "date-fns";
import {
  Plus,
  Calendar,
  DollarSign,
  Target,
  Activity,
  FileVideo,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/useToast";

const StatusBadge = ({ status }: { status: Campaign["status"] }) => {
  const variants = {
    pending: "bg-yellow-100 text-yellow-800",
    active: "bg-green-100 text-green-800",
    completed: "bg-gray-100 text-gray-800",
  };

  return (
    <Badge variant="outline" className={variants[status]}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
};

export function CampaignsList() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  console.log("Rendering CampaignsList with campaigns:", campaigns);

  useEffect(() => {
    const loadCampaigns = async () => {
      try {
        const response = await getCampaigns();
        setCampaigns(response.campaigns);
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load campaigns",
        });
      } finally {
        setLoading(false);
      }
    };

    loadCampaigns();
  }, [toast]);

  return (
    <PageContainer>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Campaigns</CardTitle>
          <CardDescription>View and manage your campaigns</CardDescription>
        </div>
        <Button onClick={() => navigate("/create")}>
          <Plus className="mr-2 h-4 w-4" />
          New Campaign
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">Loading campaigns...</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Campaign</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Budget</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Submitted</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {console.log("About to map campaigns:", campaigns.map(c => ({ id: c._id, name: c.campaignDetails.campaignName })))}
              {campaigns.map((campaign) => {
                console.log("Rendering campaign row:", {
                  _id: campaign._id,
                  campaignName: campaign.campaignDetails.campaignName,
                  rowKey: campaign._id
                });
                return (
                  <TableRow
                    key={campaign._id}
                    className="cursor-pointer hover:bg-secondary/50"
                    onClick={() => navigate(`/campaigns/${campaign._id}`)}
                  >
                    <TableCell>
                      <div>
                        <p className="font-medium">
                          {campaign.campaignDetails.campaignName}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {campaign.campaignDetails.dealerName}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={campaign.status} />
                    </TableCell>
                    <TableCell>
                      ${campaign.campaignDetails.budget.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        <Calendar className="mr-2 h-4 w-4 text-muted-foreground" />
                        {format(
                          new Date(campaign.campaignDetails.startDate),
                          "MMM d"
                        )}{" "}
                        -{" "}
                        {format(new Date(campaign.campaignDetails.endDate), "MMM d")}
                      </div>
                    </TableCell>
                    <TableCell>
                      {format(new Date(campaign.submittedAt), "MMM d, yyyy")}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </PageContainer>
  );
}